import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authClient } from '../../lib/auth-client';
import { redirectUrlFrom } from '../../lib/oauth-continue';
import { useTRPC } from '../../lib/trpc';
import { beginIdentityTransition } from '../../lib/identity';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, field } from '../create/Field';
import { BrandMark } from './BrandMark';

/**
 * The front door (ported into v2 at V5 parity — the last screen still wearing the old
 * skin, and the first one anybody sees).
 *
 * A real <form> on purpose: Enter submits, password managers fill it, and autocomplete
 * hints are set per mode. Those are free here and expensive to retrofit.
 */
/**
 * `oauthQuery` turns this same screen into the login step of an OAuth authorization —
 * the shop owner connecting Claude or ChatGPT to their data.
 *
 * It is passed straight through to Better Auth as `oauth_query`, and that is the whole
 * mechanism: the provider matches ANY request carrying that field, verifies its signature,
 * and stashes the pending authorization. Then, on the response that sets the session cookie,
 * it resumes the authorization and hands back where to go next. So the sign-in that a
 * connector triggers is the ordinary sign-in, plus one opaque string it must not lose.
 *
 * `onAuthorized` receives that next URL. Without it we would land the owner in the app and
 * silently abandon the connector they were halfway through approving.
 */
export function AuthScreen({
  oauthQuery,
  onAuthorized,
}: {
  oauthQuery?: string;
  onAuthorized?: (url: string) => void;
} = {}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  // Signing up is right for the front door, where most arrivals are new. It is wrong here:
  // an owner connecting an AI tool demonstrably already has an account.
  const [mode, setMode] = useState<'signup' | 'signin'>(oauthQuery ? 'signin' : 'signup');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      /**
       * ⚠️ ONE OBJECT, BOTH BRANCHES. `oauth_query` used to be spread separately into the
       * sign-up and sign-in calls, which meant it could be dropped from just one of them —
       * and the sign-in branch is the one that matters, since an owner connecting an AI
       * already has an account. That mutation left every test green while every real
       * signed-out authorization silently died after login. (Codex adversary, round 2, #3.)
       *
       * Built once, so there is no longer a branch to drop it from.
       */
      const credentials = {
        email,
        password,
        ...(oauthQuery ? { oauth_query: oauthQuery } : {}),
      };
      // Before the cookie can change: every other tab stops drawing the account it had
      // (MAP 225a). The gate then checks this tab's new session like any other.
      beginIdentityTransition();
      const res =
        mode === 'signup'
          ? await authClient.signUp.email({ name, ...credentials })
          : await authClient.signIn.email(credentials);
      if (res.error) {
        setError(res.error.message ?? 'Something went wrong. Try that again.');
        return;
      }
      const next = redirectUrlFrom(res.data);
      if (oauthQuery && next) {
        // A full navigation, not a router push: the destination is the authorization
        // server, and on the final hop it is the connector's own origin.
        onAuthorized?.(next);
        return;
      }
      if (oauthQuery) {
        // Signed in, but the provider did not say where to go. Sending them into the app
        // would look like success while the connector they came to approve never
        // completes — the failure this whole screen exists to avoid.
        setError(
          'Signed in, but the connection could not be resumed. Start again from the app that asked for access.',
        );
        return;
      }
      await qc.invalidateQueries({ queryKey: trpc.me.session.queryKey() });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-5 p-6">
      <div className="flex items-center gap-3">
        <BrandMark className="size-11" />
        <div>
          <h1 className="font-heading text-xl font-bold">Servwell</h1>
          {/*
            The brand map BANS "honest" as a self-description — claiming it is the one way
            to spend it. This was the last instance left in the product, on the first screen
            anybody sees. What replaces it is a claim the app can be held to.
          */}
          <p className="text-sm text-muted-foreground">
            Field service software that tells you the truth.
          </p>
        </div>
      </div>

      <SectionCard title={mode === 'signup' ? 'Create your account' : 'Welcome back'}>
        <form onSubmit={submit} className="space-y-3">
          {mode === 'signup' ? (
            <Field label="Your name">
              <input
                className={field}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
              />
            </Field>
          ) : null}
          <Field label="Email">
            <input
              className={field}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </Field>
          <Field label="Password">
            <input
              className={field}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              minLength={8}
              required
            />
          </Field>
          {mode === 'signup' ? (
            <p className="text-xs text-muted-foreground">At least 8 characters.</p>
          ) : null}
          {error ? <Notice tone="danger">{error}</Notice> : null}
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? 'One moment…' : mode === 'signup' ? 'Create account' : 'Sign in'}
          </Button>
        </form>
      </SectionCard>

      <p className="text-center text-sm text-muted-foreground">
        {mode === 'signup' ? 'Already have an account?' : 'New to Servwell?'}{' '}
        <button
          type="button"
          className="font-semibold text-[var(--sw-brand-text)] hover:underline"
          onClick={() => {
            setMode(mode === 'signup' ? 'signin' : 'signup');
            setError(null);
          }}
        >
          {mode === 'signup' ? 'Sign in' : 'Create one'}
        </button>
      </p>
    </div>
  );
}
